import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { TaxRateEntries, TaxRatesParent, TaxService, TaxTypeEnum } from '../../../data/logic/tax.service';
import {
    QuoteRequestRecipient,
    QuoteRequestRecipientGroup,
    QuoteRequestRecipientItem,
    QuoteRequestRecipientRecipeItem,
    RFQItemTaxStatus,
} from '../../../data/models';
import { SharedDataService } from '../../../data/services/shared-data.service';

export type TaxStatus = {
    name: keyof typeof RFQItemTaxStatus;
    display: string;
};

interface QuoteRequestRecipientSort {
    isAddedByRecipient: boolean;
    orderNumber: number;
}

@UntilDestroy()
@Component({
    selector: 'app-rfq-response-costingitems',
    templateUrl: './rfq-response-costingitems.component.html',
    styleUrls: ['./rfq-response-costingitems.component.scss'],
    standalone: false,
})
export class RfqResponseCostingitemsComponent implements OnInit, OnChanges {
    @Input() recipient!: QuoteRequestRecipient;
    @Output() selectedCountEvent: EventEmitter<number> = new EventEmitter<number>();
    itemsByGroup: Record<string, QuoteRequestRecipientItem[]>;
    recipeItemsByItem: Record<string, QuoteRequestRecipientRecipeItem[]>;
    costItemDict: Record<string, string>;
    groupSelectedDict: Record<string, boolean>;
    allSelected: boolean = false;
    total: number;
    tax: number;
    totalIncTax: number;
    private itemsChanged: Subject<void>;
    private itemsToUpdate: QuoteRequestRecipientItem[];

    public rfqItemTaxStatus = RFQItemTaxStatus;
    taxStatusOptions: TaxStatus[] = [];

    taxProfile: TaxRatesParent;

    constructor(
        private taxService: TaxService,
        private translate: TranslateService,
        private sharedData: SharedDataService
    ) {
        this.itemsByGroup = {};
        this.recipeItemsByItem = {};
        this.costItemDict = {};
        this.groupSelectedDict = {};
        this.total = this.tax = this.totalIncTax = 0;
        this.itemsChanged = new Subject<void>();
        this.itemsToUpdate = [];
        this.taxProfile = {
            TaxRatesDescription: this.sharedData.Common.TaxProfiles[0]?.Description,
            TaxRates: this.sharedData.Common.TaxProfiles[0]?.TaxRates,
            TaxRatesShortCode: this.sharedData.Common.TaxProfiles[0]?.ShortCode,
        };
    }

    ngOnInit(): void {
        this.taxService.allCostItemTypePairs().forEach((item) => {
            this.costItemDict[item.cit] = item.display;
        });
        this.itemsChanged.pipe(untilDestroyed(this), debounceTime(300)).subscribe(() => this.calculateTaxForItems());
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.recipient && this.recipient) {
            this.countSelection();
            this.calculateItemsByGroup();
            this.evaluateAllGroups();
            this.calculateTotals();
            this.setTaxStatusOptions();
        }
    }

    selectAll(): void {
        if (this.allSelected) {
            this.groupSelectedDict = {};
            this.recipient.quoteRequestRecipientItems?.forEach((item) => {
                item.isSelected = false;
                this.setRecipeItemsSelectedState(item, false);
            });
        } else {
            this.recipient.quoteRequestRecipientGroups?.forEach(
                (group) => (this.groupSelectedDict[group.iD_QuoteRequestRecipientGroup] = true)
            );
            this.recipient.quoteRequestRecipientItems?.forEach((item) => {
                item.isSelected = true;
                this.setRecipeItemsSelectedState(item, true);
            });
        }

        this.allSelected = !this.allSelected;
        this.countSelection();
        this.calculateTotals();
    }

    selectGroup(groupId: string): void {
        const selecting = !this.groupSelectedDict[groupId];
        this.groupSelectedDict[groupId] = selecting;
        this.itemsByGroup[groupId]?.forEach((item) => {
            item.isSelected = selecting;
            this.setRecipeItemsSelectedState(item, selecting);
        });
        if (selecting) {
            this.evaluateSelectAll();
        } else {
            this.allSelected = false;
        }

        this.countSelection();
        this.calculateTotals();
    }

    selectItem(item: QuoteRequestRecipientItem): void {
        item.isSelected = !item.isSelected;
        this.setRecipeItemsSelectedState(item, item.isSelected);
        const iD_QuoteRequestRecipientGroup = item.iD_QuoteRequestRecipientGroup;
        if (iD_QuoteRequestRecipientGroup) {
            if (this.groupSelectedDict[iD_QuoteRequestRecipientGroup]) {
                this.groupSelectedDict[iD_QuoteRequestRecipientGroup] = false;
                this.allSelected = false;
            } else if (this.itemsByGroup[iD_QuoteRequestRecipientGroup]?.every((item) => item.isSelected)) {
                this.groupSelectedDict[iD_QuoteRequestRecipientGroup] = true;
                this.evaluateSelectAll();
            }
        }

        this.countSelection();
        this.calculateTotals();
    }

    selectRecipeItem(recipeItem: QuoteRequestRecipientRecipeItem, item: QuoteRequestRecipientItem): void {
        recipeItem.isSelected = !recipeItem.isSelected;
        const iD_QuoteRequestRecipientGroup = item.iD_QuoteRequestRecipientGroup;
        if (iD_QuoteRequestRecipientGroup) {
            if (!recipeItem.isSelected) {
                this.groupSelectedDict[iD_QuoteRequestRecipientGroup] = false;
                this.allSelected = false;
            } else if (item.quoteRequestRecipientRecipeItems.every((recipeItem) => recipeItem.isSelected)) {
                if (this.itemsByGroup[iD_QuoteRequestRecipientGroup]?.every((item) => item.isSelected)) {
                    this.groupSelectedDict[iD_QuoteRequestRecipientGroup] = true;
                    this.evaluateSelectAll();
                }
            }
        }

        this.countSelection();
        this.calculateTotals();
    }

    private setRecipeItemsSelectedState(item: QuoteRequestRecipientItem, status: boolean): void {
        if (item.isRecipe) {
            item.quoteRequestRecipientRecipeItems.forEach((recipeItem) => {
                recipeItem.isSelected = status;
            });
        }
    }

    inputUpdated(item: QuoteRequestRecipientItem, value: string, recipeItem?: QuoteRequestRecipientRecipeItem): void {
        const updatedValue = +value;
        if (recipeItem) {
            recipeItem.total = updatedValue;
        } else {
            item.total = updatedValue;
        }
        if (!this.itemsToUpdate.includes(item)) {
            this.itemsToUpdate.push(item);
        }
        this.itemsChanged.next();
    }

    private countSelection(): void {
        const selectedItems = this.getSelectedItemsAndRecipeItems().length;
        this.selectedCountEvent.emit(selectedItems);
    }

    private getSelectedItemsAndRecipeItems(): (QuoteRequestRecipientItem | QuoteRequestRecipientRecipeItem)[] {
        return (
            this.recipient?.quoteRequestRecipientItems?.reduce(
                (acc: (QuoteRequestRecipientItem | QuoteRequestRecipientRecipeItem)[], item) => {
                    if (item.isRecipe) {
                        acc.push(...item.quoteRequestRecipientRecipeItems.filter((recItem) => recItem.isSelected));
                    } else if (item.isSelected) {
                        acc.push(item);
                    }
                    return acc;
                },
                []
            ) ?? []
        );
    }

    private calculateItemsByGroup(): void {
        this.itemsByGroup = {};
        this.recipeItemsByItem = {};
        if (this.recipient?.quoteRequestRecipientItems?.length) {
            if (this.recipient.quoteRequestRecipientItems.some((qrri) => !qrri.iD_QuoteRequestRecipientGroup)) {
                const uncategorisedCategory: Partial<QuoteRequestRecipientGroup> = {
                    iD_QuoteRequestRecipientGroup: 'Uncategorised',
                    description: this.translate.instant('general.uncategorised'),
                };
                this.recipient.quoteRequestRecipientItems.forEach((item) => {
                    if (!item.iD_QuoteRequestRecipientGroup) {
                        item.iD_QuoteRequestRecipientGroup = uncategorisedCategory.iD_QuoteRequestRecipientGroup;
                    }
                });

                if (this.recipient.quoteRequestRecipientGroups)
                    this.recipient.quoteRequestRecipientGroups.push(uncategorisedCategory as QuoteRequestRecipientGroup);
            }
            this.recipient.quoteRequestRecipientItems.sort(this.sortExistingItemsFirst).forEach((item) => {
                if (item.iD_QuoteRequestRecipientGroup) {
                    let groupItems = this.itemsByGroup[item.iD_QuoteRequestRecipientGroup];
                    if (!groupItems) {
                        this.itemsByGroup[item.iD_QuoteRequestRecipientGroup] = groupItems = [];
                    }
                    groupItems.push(item);
                }
                if (item.isRecipe) {
                    item.quoteRequestRecipientRecipeItems.sort(this.sortExistingItemsFirst).forEach((recipeItem) => {
                        let recipeItems = this.recipeItemsByItem[item.iD_QuoteRequestRecipientItem];
                        if (!recipeItems) {
                            this.recipeItemsByItem[item.iD_QuoteRequestRecipientItem] = recipeItems = [];
                        }
                        recipeItems.push(recipeItem);
                    });
                }
            });
        }
    }

    private sortExistingItemsFirst(l: QuoteRequestRecipientSort, r: QuoteRequestRecipientSort) {
        // Items added by the recipient always go after existing items
        if (l.isAddedByRecipient && !r.isAddedByRecipient) {
            return 1;
        } else if (!l.isAddedByRecipient && r.isAddedByRecipient) {
            return -1;
        }
        return l.orderNumber - r.orderNumber;
    }

    private calculateTaxForItems(): void {
        if (this.recipient.taxStatus === RFQItemTaxStatus.TAXINCLUDED) {
            this.itemsToUpdate.forEach((item) => {
                if (item.isRecipe) {
                    item.quoteRequestRecipientRecipeItems.forEach((recipeItem) => {
                        this.calculateTax(recipeItem);
                    });
                    this.calculateParentRecipeItemTotals(item);
                } else this.calculateTax(item);
            });
        }

        this.itemsToUpdate = [];
        this.calculateTotals();
    }

    private calculateTax(item: QuoteRequestRecipientItem | QuoteRequestRecipientRecipeItem, overrideCostItemType?: string): void {
        const taxEntries = new TaxRateEntries(this.recipient.quoteRequest.taxRates);
        taxEntries.filterByTaxType(TaxTypeEnum.Expense);
        taxEntries.filterByTaxCodes(JSON.parse(item.taxRates));
        taxEntries.filterByNonExemptCostItemType(overrideCostItemType ?? item.costItemType);
        const taxRate = taxEntries.totalTaxRate();

        switch (item.taxStatus) {
            case RFQItemTaxStatus[RFQItemTaxStatus.TAXINCLUDED]:
                item.tax = item.total - item.total / (1 + taxRate);
                item.totalIncTax = +item.total;
                break;
            case RFQItemTaxStatus[RFQItemTaxStatus.TAXEXCLUDED]:
                item.tax = item.total * taxRate;
                item.totalIncTax = item.total + item.tax;
                break;
            case RFQItemTaxStatus[RFQItemTaxStatus.TAXEXEMPT]:
                item.tax = 0;
                item.totalIncTax = +item.total;
                break;
        }
    }

    updateTaxStatus(item: QuoteRequestRecipientItem): void {
        if (item.isRecipe) {
            item.quoteRequestRecipientRecipeItems.forEach((recipeItem) => {
                recipeItem.taxStatus = item.taxStatus;
            });
        }
        this.itemsToUpdate.push(item);
        this.calculateTaxForItems();
    }

    updateRecipeItemTaxStatus(item: QuoteRequestRecipientRecipeItem, parent: QuoteRequestRecipientItem): void {
        this.calculateTax(item);
        this.calculateParentRecipeItemTotals(parent);
        this.calculateTotals();
    }

    updateTaxRates(taxRates: string, item: QuoteRequestRecipientItem): void {
        item.taxRates = taxRates;
        if (item.isRecipe) {
            item.quoteRequestRecipientRecipeItems.forEach((recipeItem) => {
                recipeItem.taxRates = taxRates;
                this.calculateTax(recipeItem, item.calculationMethod < 2 ? item.costItemType : recipeItem.costItemType);
            });
            this.calculateParentRecipeItemTotals(item);
        } else {
            this.calculateTax(item);
        }
        this.calculateTotals();
    }

    updateRecipeItemTaxRates(taxRates: string, item: QuoteRequestRecipientRecipeItem, parent: QuoteRequestRecipientItem): void {
        item.taxRates = taxRates;
        this.calculateTax(item);
        this.calculateParentRecipeItemTotals(parent);
        this.calculateTotals();
    }

    private calculateTotals(): void {
        const selectedItems = this.getSelectedItemsAndRecipeItems();
        const totals = selectedItems.reduce(
            (acc, item) => {
                acc.total += item.taxStatus === RFQItemTaxStatus[RFQItemTaxStatus.TAXINCLUDED] ? item.total - item.tax : item.total;
                acc.tax += item.tax;
                acc.totalIncTax += item.totalIncTax;
                return acc;
            },
            { total: 0, tax: 0, totalIncTax: 0 }
        );
        this.total = totals.total;
        this.tax = totals.tax;
        this.totalIncTax = totals.totalIncTax;
    }

    private calculateParentRecipeItemTotals(parent: QuoteRequestRecipientItem): void {
        const totals = parent.quoteRequestRecipientRecipeItems.reduce(
            (acc, item) => {
                acc.total += item.total;
                acc.tax += item.tax;
                acc.totalIncTax += item.totalIncTax;
                return acc;
            },
            { total: 0, tax: 0, totalIncTax: 0 }
        );
        parent.total = totals.total;
        parent.tax = totals.tax;
        parent.totalIncTax = totals.totalIncTax;
    }

    private evaluateAllGroups(): void {
        Object.keys(this.itemsByGroup).forEach((group) => {
            this.groupSelectedDict[group] = this.itemsByGroup[group]?.every((item) => item.isSelected) ?? false;
        });
        this.evaluateSelectAll();
    }

    private evaluateSelectAll(): void {
        const dictKeys = Object.keys(this.groupSelectedDict);
        this.allSelected =
            dictKeys.length === this.recipient.quoteRequestRecipientGroups?.length &&
            dictKeys.every((groupId) => this.groupSelectedDict[groupId]);
    }

    private setTaxStatusOptions(): void {
        this.taxStatusOptions = [
            { name: 'TAXINCLUDED', display: 'Inc' },
            { name: 'TAXEXCLUDED', display: 'Ex' },
        ];
        if (this.recipient.quoteRequest.calculationMethod === 0) {
            this.taxStatusOptions.push({ name: 'TAXEXEMPT', display: 'No tax' });
        }
    }
}
